import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/logo-3.png";

export default function PremiumNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // track active section for underline: "home" | "about" | "process" | "compliance" | "contact"
  const [activeSection, setActiveSection] = useState(() => {
    const p = location.pathname.replace("/", "") || "home";
    return p === "" ? "home" : p;
  });

  // tiny ref to hold whether we should update activeSection from scroll (avoid race)
  const allowScrollUpdateRef = useRef(true);
  // hamburger button, so Escape can hand keyboard focus back to it when the menu closes
  const menuButtonRef = useRef(null);

  // Smooth scroll helper (duration in ms)
  function smoothScrollTo(targetY, duration = 700) {
    const startY = window.pageYOffset;
    const distance = targetY - startY;
    const startTime = performance.now();
    // easing (easeInOutQuad)
    function ease(t) {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }
    function step(now) {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      const eased = ease(t);
      window.scrollTo(0, Math.round(startY + distance * eased));
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  // unified scrollToId that uses smoothScrollTo and offset for sticky header
  function scrollToId(id) {
    if (!id) return false;
    const el = document.getElementById(id);
    if (!el) return false;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 88; // same offset as before
    // prevent scroll-driven activeSection updates for a short while to avoid flicker
    allowScrollUpdateRef.current = false;
    smoothScrollTo(y, 700);
    // Re-enable updates after the scroll duration + small buffer
    setTimeout(() => {
      allowScrollUpdateRef.current = true;
      // set active section explicitly after scroll ends
      setActiveSection(id);
    }, 800);
    return true;
  }

  function handleNavClick(target) {
    setOpen(false);

    if (["process", "compliance", "blogs"].includes(target)) {
  setActiveSection(target);
  navigate(`/${target}`);
  return;
}


    // For anchors on the home page
    if (location.pathname === "/") {
      // set active immediately for UX
      setActiveSection(target);
      scrollToId(target);
      return;
    }

    // Not on '/', navigate to '/' and pass intended anchor via state
    navigate("/", { state: { scrollTo: target } });
    // Also set activeSection so underline shows immediately (will be validated by Home's scroll code after nav)
    setActiveSection(target);
  }

  // ripple helper for CTA (kept unchanged)
  function createRipple(e) {
    const btn = e.currentTarget;
    const circle = document.createElement("span");
    const diameter = Math.max(btn.clientWidth, btn.clientHeight);
    const radius = diameter / 2;
    circle.classList.add("ripple-circle");
    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${e.clientX - btn.getBoundingClientRect().left - radius}px`;
    circle.style.top = `${e.clientY - btn.getBoundingClientRect().top - radius}px`;
    btn.appendChild(circle);
    setTimeout(() => {
      circle.remove();
    }, 650);
  }

  const menuItems = [
    { key: "home", label: "Home" },
    { key: "about", label: "About" },
    { key: "process", label: "Investment Process" },
    { key: "compliance", label: "Compliance" },
    { key: "contact", label: "Contact" },
    { key: "blogs", label: "Blogs" },
    { key: "testimonials", label: "Testimonials" },
  ];

  // When location changes (route navigation), if we are on other pages mark activeSection accordingly
  useEffect(() => {
    const p = location.pathname.replace("/", "") || "home";
    if (p && p !== "home" && p !== "") {
      setActiveSection(p);
    } else {
      // if on home, keep whichever is already set (Home will update it based on scroll)
      setActiveSection((prev) => prev || "home");
    }
  }, [location.pathname]);

  // When on the home page, observe scroll to update which section is active
  useEffect(() => {
    if (location.pathname !== "/") return;

    const ids = ["home", "about", "testimonials", "contact"];
    function updateFromScroll() {
      if (!allowScrollUpdateRef.current) return;
      const offset = 120; // when top of section crosses this threshold, count as active
      let found = "home";
      for (let id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        // If element top is above offset and bottom is below offset, it's active
        if (rect.top <= offset && rect.bottom > offset) {
          found = id;
          break;
        }
      }
      setActiveSection(found);
    }

    // run on mount
    updateFromScroll();
    window.addEventListener("scroll", updateFromScroll, { passive: true });
    window.addEventListener("resize", updateFromScroll);

    // in case content changes and we need to recalc
    const ro = new MutationObserver(updateFromScroll);
    ro.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener("scroll", updateFromScroll);
      window.removeEventListener("resize", updateFromScroll);
      ro.disconnect();
    };
  }, [location.pathname]);

  const path = location.pathname.replace("/", "") || "home";

  return (
    <header className="sticky top-0 z-50 nav-glass">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
  setOpen(false);

 if (location.pathname === "/") {
  allowScrollUpdateRef.current = false;

  smoothScrollTo(0, 700);

  setTimeout(() => {
    allowScrollUpdateRef.current = true;
    setActiveSection("home");
  }, 800);
} else {
  navigate("/");
}

}}
            className="inline-flex items-center gap-3"
            aria-label="APAR Home"
          >
            <img src={logo} alt="" className="h-20 w-22 object-contain" />
            {/* <div>
              <div className="text-lg font-semibold h-font" style={{ color: "var(--brand-deep)" }}>APAR</div>
              <div className="text-xs text-green-600 b-font">Growth Partners</div>
            </div> */}
          </button>
        </div>

        <nav className="hidden md:flex items-center gap-6 text-sm b-font" aria-label="Primary navigation">
          {menuItems.map(({ key, label }) => {
            // Determine active: if we're on a different route, use path; otherwise (home) use activeSection
            const isActive =
              (location.pathname === "/"
                ? activeSection === key
                : (key === path || (key === "home" && path === "")));

            return (
              <button
                key={key}
                onClick={() => handleNavClick(key)}
                className={`nav-underline ${isActive ? "active" : ""} px-1 py-1`}
                aria-current={isActive ? "page" : undefined}
              >
                {label}
                <span className="nav-dot" aria-hidden="true" />
              </button>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button
            onMouseDown={createRipple}
            onClick={() => handleNavClick("contact")}
            className="ani-cta cta-glow ripple"
            aria-label="Get in touch"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="opacity-90" aria-hidden="true">
              <path d="M21 12.5c0 4.694-3.806 8.5-8.5 8.5S4 17.194 4 12.5 7.806 4 12.5 4 21 7.806 21 12.5z" stroke="white" strokeWidth="1.2"/>
            </svg>
            <span>Get In Touch</span>
          </button>
        </div>

        <div
          className="md:hidden relative"
          onKeyDown={(e) => {
            if (e.key === "Escape" && open) {
              setOpen(false);
              if (menuButtonRef.current) menuButtonRef.current.focus();
            }
          }}
        >
          <button
            ref={menuButtonRef}
            onClick={() => setOpen(v => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
            className="p-2 rounded"
          >
            <span className={`hb ${open ? "open" : ""}`} aria-hidden="true">
              <span></span><span></span><span></span>
            </span>
          </button>

          {open && (
            <div id="mobile-menu" className="absolute right-3 top-16 bg-white border shadow-lg p-4 w-56 rounded-lg">
              {menuItems.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => { handleNavClick(key); setOpen(false); }}
                  className="block w-full text-left py-2 text-sm"
                >
                  {label}
                </button>
              ))}
              <div className="mt-3 border-t pt-3">
                <button
                  onClick={() => { handleNavClick("contact"); setOpen(false); }}
                  className="ani-cta ripple w-full justify-center"
                  onMouseDown={createRipple}
                >
                  <span>Contact</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
